import type { Ability, AbilityCtx, PlayerRuntime } from '@md/engine';
import { aabbCenter, bodyAabb, tileContacts } from '@md/engine';
import {
  MAGNET_DRAIN_PER_SECOND,
  MAGNET_ENERGY_MAX,
  MAGNET_ENERGY_REARM,
  MAGNET_RECHARGE_PER_SECOND,
  POLARITY_INVERT_TICKS,
} from '@md/shared';
import { BASE_POLARITY } from '../characters.js';
import { TILE_DEGAUSS_CHAR, TILE_INVERTER_CHAR } from '../tiles.js';

/**
 * Ability « champ magnétique » — le cœur de Magnetic Duo (concept §3).
 *
 * Elle ne calcule AUCUNE force : elle décide seulement, pour chaque robot et
 * chaque tick, l'état à partir duquel les règles d'interaction travailleront —
 *
 *  1. **jauge d'énergie** : se vide tant que le champ est actif, se recharge au
 *     repos, coupe le champ à zéro et interdit la réactivation avant le seuil
 *     de ré-armement (concept §3.3) ;
 *  2. **zone dégaussante `~`** : à l'intérieur, aucun champ possible — et donc
 *     aucune consommation (concept §3.2) ;
 *  3. **polarité** : inversée temporairement par une dalle `!` (concept §3.1) ;
 *  4. **accroche `s`/`c`** : tant que le champ est actif et que le robot touche
 *     une surface de SON métal, la gravité est coupée. Relâcher = tomber.
 *
 * Les drapeaux publiés dans `player.flags` sont numériques (donc sérialisables
 * tels quels dans le snapshot) : `polarity`, `inverted`, `invertTicks`,
 * `clinging`, `clingSide`, `degaussed`, `depleted`.
 */

/** Côté d'accroche encodé numériquement pour le snapshot et le rendu. */
export const CLING_SIDE = Object.freeze({
  none: 0,
  above: 1,
  below: 2,
  left: 3,
  right: 4,
});

const CLING_SIDE_BY_NAME: Readonly<Record<string, number>> = CLING_SIDE;

/** Le robot est-il dans une zone dégaussante (test sur son centre) ? */
export function isDegaussed(player: PlayerRuntime, level: AbilityCtx['level']): boolean {
  const center = aabbCenter(bodyAabb(player.body));
  return level.tileAt(Math.floor(center.x), Math.floor(center.y)).char === TILE_DEGAUSS_CHAR;
}

/** Polarité effective d'un robot : pôle de base × inversion en cours. */
export function polarityOf(player: PlayerRuntime): number {
  return player.flags.polarity ?? BASE_POLARITY[player.character.id] ?? 1;
}

function updatePlayer(ctx: AbilityCtx): void {
  const { player } = ctx;
  const base = BASE_POLARITY[player.character.id] ?? 1;

  // --- Polarité : la dalle `!` réarme un compte à rebours d'inversion -------
  let invertTicks = Math.max(0, (player.flags.invertTicks ?? 0) - 1);
  const contacts = tileContacts(player.body, ctx.level);
  for (const contact of contacts) {
    if (contact.tile.char === TILE_INVERTER_CHAR) {
      invertTicks = POLARITY_INVERT_TICKS;
      break;
    }
  }
  player.flags.invertTicks = invertTicks;
  player.flags.inverted = invertTicks > 0 ? 1 : 0;
  player.flags.polarity = invertTicks > 0 ? -base : base;

  // --- Zone dégaussante ----------------------------------------------------
  const degaussed = isDegaussed(player, ctx.level);
  player.flags.degaussed = degaussed ? 1 : 0;

  // --- Jauge d'énergie -----------------------------------------------------
  const depleted = (player.flags.depleted ?? 0) === 1;
  const wants = ctx.active && !degaussed && !player.dead;
  const active = wants && player.energy > 0 && !depleted;

  if (active) {
    player.energy = Math.max(0, player.energy - MAGNET_DRAIN_PER_SECOND * ctx.dt);
    player.flags.depleted = player.energy <= 0 ? 1 : 0;
  } else if (!wants) {
    // La jauge ne remonte QUE touche relâchée : rester appuyé après la coupure
    // ne recharge rien, il faut vraiment lâcher le champ (concept §3.3).
    player.energy = Math.min(
      MAGNET_ENERGY_MAX,
      player.energy + MAGNET_RECHARGE_PER_SECOND * ctx.dt,
    );
    if (depleted && player.energy >= MAGNET_ENERGY_REARM) {
      player.flags.depleted = 0;
    }
  }
  player.fieldActive = active;

  // --- Accroche aux surfaces magnétiques -----------------------------------
  let clingSide = 0;
  if (active) {
    for (const contact of contacts) {
      if (
        contact.side === 'inside' ||
        !player.character.clingSurfaces.includes(contact.tile.char)
      ) {
        continue;
      }
      clingSide = CLING_SIDE_BY_NAME[contact.side] ?? 0;
      break;
    }
  }
  player.flags.clinging = clingSide === 0 ? 0 : 1;
  player.flags.clingSide = clingSide;
  if (clingSide !== 0) {
    player.gravityScale = 0;
    player.body.vy = 0;
  }
}

export const magnetFieldAbility: Ability = {
  id: 'magnet-field',
  input: 'action',
  update: updatePlayer,
};
